import { useEffect } from "react";
import {
  createYoutubeLinkedTextDelta,
  hasYoutubeTextSegment,
} from "../../utils/youtubePreview.util";

const PASTE_EVENT = "paste";
const PASTE_CAPTURE_PHASE = true;
const PLAIN_TEXT_MIME_TYPE = "text/plain";
const HTML_MIME_TYPE = "text/html";

const getEditor = (quillRef) => quillRef.current?.getEditor?.() ?? null;

const getClipboardText = (event) =>
  event.clipboardData?.getData(PLAIN_TEXT_MIME_TYPE) ?? "";

const hasClipboardHtml = (event) =>
  Boolean(event.clipboardData?.getData(HTML_MIME_TYPE));

const getSelectionRange = (quill) => quill.getSelection(true);

const buildPasteDelta = ({ Delta, quill, range, text }) => {
  const attributes = quill.getFormat(range.index);
  const pastedDelta = createYoutubeLinkedTextDelta({ Delta, attributes, text });

  return {
    nextSelectionIndex: range.index + pastedDelta.length(),
    updateDelta: new Delta()
      .retain(range.index)
      .delete(range.length)
      .concat(pastedDelta),
  };
};

const insertLinkedYoutubeText = ({
  Delta,
  quill,
  range,
  silentSource,
  text,
  userSource,
}) => {
  const { nextSelectionIndex, updateDelta } = buildPasteDelta({
    Delta,
    quill,
    range,
    text,
  });

  quill.updateContents(updateDelta, userSource);
  quill.setSelection(nextSelectionIndex, silentSource);
  quill.scrollSelectionIntoView?.();
};

const handlePlainTextPaste = ({ Delta, event, quill, silentSource, userSource }) => {
  if (hasClipboardHtml(event)) return;

  const text = getClipboardText(event);
  if (!hasYoutubeTextSegment(text)) return;

  const range = getSelectionRange(quill);
  if (!range) return;

  event.preventDefault();
  insertLinkedYoutubeText({
    Delta,
    quill,
    range,
    silentSource,
    text,
    userSource,
  });
};

const useYoutubeAutoLinkPaste = ({
  Delta,
  quillRef,
  silentSource,
  userSource,
}) => {
  useEffect(() => {
    const quill = getEditor(quillRef);
    const editorRoot = quill?.root;
    if (!quill || !editorRoot) return undefined;

    const handlePaste = (event) => {
      handlePlainTextPaste({ Delta, event, quill, silentSource, userSource });
    };

    editorRoot.addEventListener(PASTE_EVENT, handlePaste, PASTE_CAPTURE_PHASE);

    return () => {
      editorRoot.removeEventListener(PASTE_EVENT, handlePaste, PASTE_CAPTURE_PHASE);
    };
  }, [Delta, quillRef, silentSource, userSource]);
};

export default useYoutubeAutoLinkPaste;
